import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { Flex } from '@dutchie/common/components/rebass-compat';
import _ from 'lodash';

import { useObserver } from 'mobx-react';
import ReactSelect from 'shared/components/react-select';

import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import {
  RadioGroup,
  CheckoutAccordion,
  CheckoutAccordionActions,
  CheckoutAccordionSummary,
  Image,
} from 'shared/components';

import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import { useGetAvailableReservations } from 'src/state/actions/cart';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  filterOpenDays,
  findSelectedDayIndex,
  getAdvancedDayOrderingDays,
  getAdvancedDayOrderingTimes,
  sameDayDeliveryAdjustedOrderingDays,
} from 'shared/helpers/order-reservations';
import { getServiceEstimateForCart } from 'shared/helpers/order-estimates';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import { shouldHideOrderEstimates } from 'shared/helpers/dispensaries';
import { usePosFeesConfig } from 'src/components/modals/order-type/use-pos-fees-config';
import {
  formatPosDeliveryFeeForSchedule,
  formatPosDeliveryFeeForZone,
  getPosDeliveryFlatFeeForSchedule,
} from 'src/components/modals/order-type/pos-fee-display';

function ReactSelectOptionLabel({ label, isDisabled, labelPrefix }) {
  return (
    <span>
      <>
        {labelPrefix}: <Bold>{label}</Bold>
        {isDisabled ? ' (Sold out)' : ''}
      </>
    </span>
  );
}

function ReactSelectOptionLabelAdvancedDay({ label, isDisabled }) {
  return (
    <span>
      <>
        {`${label} `}
        {isDisabled ? (
          <>
            <br />
            <span className='sold-out'>(Sold out)</span>
          </>
        ) : (
          ''
        )}
      </>
    </span>
  );
}

const NoOptionsAvailable = (message) => <NoOptionsMessage>{message}</NoOptionsMessage>;

const getPrefix = (advancedDayOrderingEnabled, formattedOption, { extendedDayOrdering, selectedDayLabel } = {}) => {
  if (
    advancedDayOrderingEnabled &&
    formattedOption.labelPrefix !== 'Today' &&
    formattedOption.labelPrefix !== 'Tomorrow'
  ) {
    if (extendedDayOrdering && selectedDayLabel) {
      return selectedDayLabel;
    }
    return formattedOption.dayOfWeek;
  }
  return formattedOption.labelPrefix;
};

const SELECT_CONTAINER_STYLES = {
  display: 'flex',
  width: '100%',
};

const SELECT_OPTION_STYLES = {
  height: '40px',
  padding: 'unset',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '20px',
};

function ScheduledOrders(_props, ref) {
  const { customized } = useTheme();
  const Checkout = useCheckout();
  const { Cart } = useStores();
  const amplitude = useAmplitude();
  const flags = useFlags();
  const dispensary = useObserver(() => Cart?.dispensary || Cart?.order?.dispensary);
  const getAvailableReservations = useGetAvailableReservations();
  const checkoutReservationOptions = useObserver(() => Cart.checkoutReservationOptions);
  const reservation = useObserver(() => Cart.order.reservation);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState();
  const [tempIndex, setTempIndex] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const orderType = useObserver(() => (Cart.orderType === 'pickup' ? 'inStorePickup' : Cart.orderType));
  const cartAddress = useObserver(() => Cart.address);
  const selectedOrderTypeConfigs = dispensary.orderTypesConfigV2[orderType];

  const sameDayDeliveryEnabled = selectedOrderTypeConfigs?.scheduledOrderingConfigV2?.sameDayDeliveryEnabled;
  const asapAndScheduledAvailable =
    selectedOrderTypeConfigs?.enableASAPOrdering && selectedOrderTypeConfigs?.enableScheduledOrdering;
  const showAsapOption = asapAndScheduledAvailable && !Cart.isAfterHoursOrder;
  const disableScheduledOrdering = !cartAddress.ln1 && orderType === 'delivery';
  const scheduledLabel = disableScheduledOrdering ? 'Scheduled For Later (Address required)' : 'Scheduled For Later';

  // POS-configured delivery fees (delivery-only). Read up here so we can default the Time box
  // to the cheaper option below.
  const posFeesEnabled = flags['core.cats-ecomm.pos-fee-config-display.rollout'] && orderType === 'delivery';
  const { deliveryFees } = usePosFeesConfig();

  // Default to whichever delivery option is cheaper. Only flat all-zones fees are comparable
  // pre-cart; when Scheduled is the cheaper default we preselect the earliest slot (below) so
  // it can be saved. Falls back to today's behavior when fees aren't comparable.
  const asapFlatFee = posFeesEnabled ? getPosDeliveryFlatFeeForSchedule(deliveryFees, 'asap') : null;
  const scheduledFlatFee = posFeesEnabled ? getPosDeliveryFlatFeeForSchedule(deliveryFees, 'scheduled') : null;
  const scheduledIsCheaper =
    asapAndScheduledAvailable &&
    !disableScheduledOrdering &&
    asapFlatFee != null &&
    scheduledFlatFee != null &&
    scheduledFlatFee < asapFlatFee;
  const fallbackSchedulingOption = showAsapOption ? 'asap' : 'scheduled';
  const defaultSchedulingOption = scheduledIsCheaper ? 'scheduled' : fallbackSchedulingOption;
  const [selectedSchedulingOption, setSelectedSchedulingOption] = useState(defaultSchedulingOption);
  // Tracks whether the shopper has manually changed the ASAP/Scheduled choice, so applying the
  // (async-resolved) cheaper default never overrides their pick.
  const userPickedSchedulingRef = useRef(false);
  const reservationType = useObserver(() => Cart.reservationType);

  const advancedDayOrderingEnabled = selectedOrderTypeConfigs?.scheduledOrderingConfigV2.advancedDayOrderingEnabled;
  const advancedDayOrderingLimit = selectedOrderTypeConfigs?.scheduledOrderingConfigV2.advancedDayOrderingLimit;
  const hideClosedDays = selectedOrderTypeConfigs?.scheduledOrderingConfigV2?.hideClosedDays;
  const hideClosedDaysEnabled = flags['ecomm.hide-closed-days-checkout.rollout'];
  const extendedDayOrdering = (advancedDayOrderingLimit ?? 6) > 6;

  const timezone = dispensary?.timezone;
  const orderTypeDisplayName = orderType === 'delivery' ? orderType : 'pickup';
  const serviceEstimate = getServiceEstimateForCart(Cart);

  const { updateCartSchedulingInfo } = _props;
  const hideOrderEstimates = shouldHideOrderEstimates(dispensary, Cart, true);
  const hideDeliveryEstimates = hideOrderEstimates && orderType === 'delivery';

  // Surface each scheduling option's POS-configured delivery fee inline, so the shopper sees
  // the ASAP vs Scheduled fee difference before selecting (no cart calc required).
  // Once the cart is priced, the POS pricing engine resolves the delivery zone(s) for the
  // address; use them to show the exact applicable fee. Before that, show the all-zones fee.
  const resolvedZoneIds = useObserver(() => Cart.costBreakdown?.resolvedZoneIds ?? []);
  // Subtotal in dollars (same units as the fee thresholds) so threshold-gated fees are
  // included exactly when the POS would charge them.
  const pricedSubtotal = useObserver(() => Cart.costBreakdown?.subtotal ?? null);
  const feeLabelForSchedule = (schedule) => {
    if (!posFeesEnabled) return null;
    return resolvedZoneIds.length > 0
      ? formatPosDeliveryFeeForZone(deliveryFees, resolvedZoneIds, schedule, pricedSubtotal)
      : formatPosDeliveryFeeForSchedule(deliveryFees, schedule);
  };
  const asapFeeLabel = feeLabelForSchedule('asap');
  const scheduledFeeLabel = feeLabelForSchedule('scheduled');

  const asapEstimate = hideDeliveryEstimates ? '' : `(${serviceEstimate}s)`;
  // When a POS fee label applies, render the option as a flex row so the fee sits
  // right-aligned (not overlapping the label). RadioGroup's LabelComponent branch skips its
  // default data-testid markup, so reproduce it here — and attach LabelComponent only when a
  // fee label exists so the flag-off DOM (and its testids) is exactly the legacy output.
  // The plain-string `sublabel` is kept for the collapsed accordion summary.
  const renderOption = (label, estimate, feeLabel, dataCyName) =>
    function OptionLabel() {
      return (
        <OptionRow>
          <span>
            <OptionBold data-cy={dataCyName} data-test={dataCyName} data-testid={dataCyName}>
              {label}
            </OptionBold>
            {estimate ? ` ${estimate}` : ''}
          </span>
          <OptionFee>{feeLabel}</OptionFee>
        </OptionRow>
      );
    };
  const schedulingOptions = [
    {
      label: 'ASAP',
      sublabel: asapEstimate,
      ...(asapFeeLabel && { LabelComponent: renderOption('ASAP', asapEstimate, asapFeeLabel, 'asap-option') }),
      value: 'asap',
      disabled: Cart.isAfterHoursOrder,
      dataCyName: 'asap-option',
    },
    {
      label: scheduledLabel,
      sublabel: '',
      ...(scheduledFeeLabel && {
        LabelComponent: renderOption(scheduledLabel, '', scheduledFeeLabel, 'scheduled-option'),
      }),
      value: 'scheduled',
      disabled: disableScheduledOrdering,
      dataCyName: 'scheduled-option',
    },
  ];

  useEffect(() => {
    // Guard on defaultSchedulingOption to avoid calling setReservationType with a falsy value
    if (!reservationType && defaultSchedulingOption) {
      Cart.setReservationType(defaultSchedulingOption);
    }
  }, [Cart, defaultSchedulingOption, reservationType]);

  // The cheaper default depends on POS fees that load async, so re-apply it whenever it
  // resolves/changes — but only until the shopper picks an option themselves.
  useEffect(() => {
    if (!userPickedSchedulingRef.current && !Cart.freezeDeliveryInfo) {
      setSelectedSchedulingOption(defaultSchedulingOption);
    }
  }, [Cart, defaultSchedulingOption]);

  const advancedDayOrderingDays = useMemo(() => {
    if (advancedDayOrderingEnabled) {
      if (!sameDayDeliveryEnabled && orderType === 'delivery') {
        return sameDayDeliveryAdjustedOrderingDays(advancedDayOrderingLimit, timezone, extendedDayOrdering);
      }
      return getAdvancedDayOrderingDays({
        advancedDayOrderingLimit,
        timezone,
        extendedDayOrdering,
      });
    }
    return [];
  }, [
    advancedDayOrderingEnabled,
    advancedDayOrderingLimit,
    orderType,
    sameDayDeliveryEnabled,
    timezone,
    extendedDayOrdering,
  ]);

  const visibleDays = useMemo(() => {
    if (advancedDayOrderingEnabled && hideClosedDays && hideClosedDaysEnabled) {
      return filterOpenDays({ advancedDayOrderingDays, checkoutReservationOptions, extendedDayOrdering, timezone });
    }
    return advancedDayOrderingDays;
  }, [
    advancedDayOrderingDays,
    checkoutReservationOptions,
    hideClosedDays,
    hideClosedDaysEnabled,
    extendedDayOrdering,
    timezone,
    advancedDayOrderingEnabled,
  ]);

  const reservationDropdownOptions = useMemo(() => {
    if (advancedDayOrderingEnabled) {
      return getAdvancedDayOrderingTimes({
        advancedDayOrderingDays: visibleDays,
        reservationOptions: checkoutReservationOptions,
        selectedDayIndex,
        extendedDayOrdering,
        timezone,
      });
    }
    return checkoutReservationOptions;
  }, [
    visibleDays,
    checkoutReservationOptions,
    selectedDayIndex,
    advancedDayOrderingEnabled,
    extendedDayOrdering,
    timezone,
  ]);

  const reservationsLoaded = checkoutReservationOptions.length > 0;
  const allDaysClosed =
    advancedDayOrderingEnabled &&
    hideClosedDays &&
    hideClosedDaysEnabled &&
    reservationsLoaded &&
    visibleDays.length === 0;

  const existingReservationIndex = _.findIndex(checkoutReservationOptions, {
    startTimeISO: reservation?.startTimeISO,
  });

  const [open, setOpen] = useState(!existingReservationIndex);

  getAvailableReservations();

  useEffect(() => {
    if (!Cart.freezeDeliveryInfo) {
      // Order-type/address change is a fresh context — let the cheaper default reapply.
      userPickedSchedulingRef.current = false;
      resetReservation();
      setSelectedSchedulingOption(defaultSchedulingOption);
      updateCartSchedulingInfo(defaultSchedulingOption, checkoutReservationOptions);
      const schedulingDisabled = !cartAddress.ln1 && orderType === 'delivery';
      setOpen(!schedulingDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, cartAddress]);

  useEffect(() => {
    if (existingReservationIndex >= 0) {
      const checkoutReservationObject = checkoutReservationOptions?.[existingReservationIndex];
      setSelectedIndex(existingReservationIndex);
      setSelectedReservation(checkoutReservationObject?.reservation);
      const newSelectedDayIndex = findSelectedDayIndex({
        advancedDayOrderingDays: visibleDays,
        reservationOption: checkoutReservationObject,
        extendedDayOrdering,
        timezone,
      });
      if (newSelectedDayIndex >= 0) {
        setSelectedDayIndex(newSelectedDayIndex);
      }
      if (_.isNil(tempIndex)) {
        setOpen(false);
      }
    }
  }, [existingReservationIndex, checkoutReservationOptions, visibleDays, tempIndex, extendedDayOrdering, timezone]);

  useEffect(() => {
    if (selectedDayIndex >= visibleDays.length && visibleDays.length > 0) {
      setSelectedDayIndex(0);
    }
  }, [visibleDays, selectedDayIndex]);

  // Whenever Scheduled is selected with nothing chosen yet — whether it's the cheaper default
  // or the shopper just switched to it from ASAP — preselect the earliest available slot in the
  // dropdown. This only sets local selection state; it does NOT commit to the Cart or close the
  // modal — the shopper still confirms with SAVE. Guarded so it never overrides an existing
  // reservation or a manual pick, and gated on the POS-fees flag so flag-off behavior is
  // untouched (the flag is this feature's rollback lever).
  useEffect(() => {
    if (
      posFeesEnabled &&
      selectedSchedulingOption === 'scheduled' &&
      existingReservationIndex < 0 &&
      _.isNil(selectedIndex) &&
      reservationDropdownOptions?.length
    ) {
      const earliest = reservationDropdownOptions[0];
      if (earliest && !_.isNil(earliest.value)) {
        setSelectedIndex(earliest.value);
        setSelectedReservation(earliest.reservation);
      }
    }
  }, [posFeesEnabled, selectedSchedulingOption, reservationDropdownOptions, existingReservationIndex, selectedIndex]);

  function resetReservation() {
    setSelectedIndex(null);
    setSelectedDayIndex(0);
    setSelectedReservation(null);
    setTempIndex(null);
  }

  const handleError = () => {
    Checkout.addErrors([{ path: 'reservation', message: 'Please select a time for this order.' }]);
    setOpen(true);
    return false;
  };

  const handleDayChange = (option) => {
    const { value } = option;
    setSelectedIndex(null);
    setSelectedDayIndex(value);
  };

  const handleTimeChange = (option) => {
    const { value } = option;
    const selectedOption = checkoutReservationOptions[value];

    setSelectedReservation(selectedOption?.reservation);
    setSelectedIndex(selectedOption.value);
    Checkout.removeError({ match: 'reservation' });
  };

  const handleClose = () => {
    Checkout.removeError({ match: 'reservation' });
    setOpen(false);
  };

  const handleSave = async () => {
    // SAVE confirms the shopper's scheduling choice (checkout also autosaves Time on
    // submit), so treat it like a manual pick: the async cheaper-default effect must
    // never flip a confirmed ASAP order to Scheduled after POS fees resolve.
    userPickedSchedulingRef.current = true;
    if (selectedSchedulingOption === 'scheduled') {
      amplitude.log(AmplitudeEvents.checkout.clickSave, {
        componentName: `Time Slot`,
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
      });
      if (_.isNil(selectedIndex) || (!selectedReservation && _.isEmpty(Cart.order.reservation))) {
        handleError();
        throw new Error(`onSave error: 'Please select a time for this order.'`);
      }
      Cart.setReservation(selectedReservation);
    } else {
      amplitude.log(AmplitudeEvents.checkout.clickSave, {
        componentName: `Asap Order`,
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
      });
      Cart.setReservation(null);
    }

    Cart.setReservationType(selectedSchedulingOption);
    updateCartSchedulingInfo(selectedSchedulingOption, checkoutReservationOptions);
    setTempIndex(null);
    handleClose();
  };

  useImperativeHandle(ref, () => ({ handleSave }));

  const handleCancel = () => {
    // must have a reservation to cancel changes.
    // otherwise show ernie
    if (selectedSchedulingOption === 'scheduled') {
      if (_.isEmpty(Cart.order.reservation) && _.isNil(tempIndex)) {
        return handleError();
      }
      if (!_.isNil(tempIndex)) {
        setSelectedIndex(tempIndex);
        const newSelectedReservation = checkoutReservationOptions?.[tempIndex]?.reservation;
        setSelectedReservation(newSelectedReservation);
        Cart.setReservation(newSelectedReservation);
        setTempIndex(null);
      } else {
        Cart.setReservation(null);
      }
    }
    return handleClose();
  };

  const handleSchedulingOptionChange = (value) => {
    userPickedSchedulingRef.current = true;
    resetReservation();
    setSelectedSchedulingOption(value);
    updateCartSchedulingInfo(value, checkoutReservationOptions);
  };

  const formattedOption = checkoutReservationOptions[selectedIndex];
  // Bind the time dropdown to the selected slot so a preselected/auto-selected reservation is
  // actually shown (react-select is otherwise uncontrolled and renders the placeholder).
  const selectedTimeOption = _.find(reservationDropdownOptions, { value: selectedIndex }) ?? null;
  const timeSelectionText = Cart.orderType === 'delivery' ? 'your order delivered' : 'to pickup your order';
  const timeSummaryLabel = _.find(schedulingOptions, { value: selectedSchedulingOption })?.label;
  const timeSummarySubLabel = _.find(schedulingOptions, { value: selectedSchedulingOption })?.sublabel;
  const summaryFeeLabel = selectedSchedulingOption === 'asap' ? asapFeeLabel : scheduledFeeLabel;

  let schedulingHeaderText = `Select a ${orderTypeDisplayName} time:`;
  if (disableScheduledOrdering && !showAsapOption) {
    schedulingHeaderText = 'Enter a delivery address to see available times.';
  } else if (showAsapOption) {
    schedulingHeaderText = `When would you like ${timeSelectionText}?`;
  }

  const summaryDayLabel = visibleDays[selectedDayIndex]?.label ?? advancedDayOrderingDays[selectedDayIndex]?.label;

  return useObserver(() => (
    <CheckoutLabelGrouping mb='20px' id='scheduled-orders' data-testid='scheduled-orders-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Time</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <CheckoutAccordion
          dataCyName='order-type'
          expanded={open}
          hideToggles={disableScheduledOrdering && !showAsapOption}
          defaultButton={!(disableScheduledOrdering && !showAsapOption)}
          onExpand={() => {
            if (!open && selectedIndex) {
              setTempIndex(selectedIndex);
              Cart.setReservation(null);
            }
            setOpen(!open);
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          error={Checkout.getError({ card: 'scheduled-orders' }).error}
          crossfade
        >
          <CheckoutAccordionSummary>
            <Flex mb={0.5} width='100%'>
              {!open && (
                <>
                  <Image src='/icons/clock.svg' width='20px' mr='11px' />
                  <SummaryText>
                    {checkoutReservationOptions[selectedIndex] && (
                      <>
                        <span>
                          {`${getPrefix(advancedDayOrderingEnabled, formattedOption, {
                            extendedDayOrdering,
                            selectedDayLabel: summaryDayLabel,
                          })}:`}
                        </span>
                        <strong>&nbsp;{formattedOption?.label}</strong>
                      </>
                    )}
                    {!checkoutReservationOptions[selectedIndex] && (
                      <>
                        <strong>{timeSummaryLabel}</strong>
                        {timeSummarySubLabel && <span> {timeSummarySubLabel}</span>}
                      </>
                    )}
                  </SummaryText>
                  {summaryFeeLabel && <CollapsedFee>{summaryFeeLabel}</CollapsedFee>}
                </>
              )}
            </Flex>
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <InnerSummaryText fontSize={13}>{schedulingHeaderText}</InnerSummaryText>
            {showAsapOption && (
              <RadioGroup
                options={schedulingOptions}
                hasSublabels
                display='block'
                defaultValue='asap'
                checked={selectedSchedulingOption}
                onChange={handleSchedulingOptionChange}
                mb='12px'
                name='orderTime'
                showChecksWhenDisabled
                required
              />
            )}
            {!(showAsapOption && selectedSchedulingOption === 'asap') && !disableScheduledOrdering && (
              <SelectionContainerV2
                advancedDayOrderingEnabled={advancedDayOrderingEnabled}
                selectedSchedulingOption={selectedSchedulingOption}
              >
                {allDaysClosed && (
                  <NoAvailableDatesMessage role='status'>
                    No {orderTypeDisplayName} dates are currently available. Please check back later.
                  </NoAvailableDatesMessage>
                )}
                {advancedDayOrderingEnabled && !allDaysClosed && (
                  <SelectContainer advancedDayOrderingEnabled={advancedDayOrderingEnabled}>
                    <ReactSelect
                      onChange={handleDayChange}
                      placeholder='Select a day...'
                      options={visibleDays}
                      noOptionsMessage={() => NoOptionsAvailable(`No days available`)}
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      formatOptionLabel={
                        advancedDayOrderingEnabled ? ReactSelectOptionLabelAdvancedDay : ReactSelectOptionLabel
                      }
                      borderRadius={customized.radius.sm}
                      containerStyles={SELECT_CONTAINER_STYLES}
                      optionStyles={SELECT_OPTION_STYLES}
                      customSelectedBackgroundColor='var(--dcm-color-surface-hover, #F6F6F6)'
                      customSelectedColor='var(--dcm-checkout-text-color, #5D666D)'
                      width='100%'
                      tagPrefix='day-select'
                    />
                  </SelectContainer>
                )}
                {!allDaysClosed && (
                  <SelectContainer
                    advancedDayOrderingEnabled={advancedDayOrderingEnabled}
                    hasError={!!Checkout.getError({ match: 'reservation' }).error}
                  >
                    <ReactSelect
                      placeholder={advancedDayOrderingEnabled ? 'Select a time...' : 'Please select a time...'}
                      onChange={handleTimeChange}
                      value={selectedTimeOption}
                      options={reservationDropdownOptions}
                      noOptionsMessage={() => NoOptionsAvailable(`No times available`)}
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      formatOptionLabel={
                        advancedDayOrderingEnabled ? ReactSelectOptionLabelAdvancedDay : ReactSelectOptionLabel
                      }
                      borderRadius={customized.radius.sm}
                      containerStyles={SELECT_CONTAINER_STYLES}
                      optionStyles={SELECT_OPTION_STYLES}
                      customSelectedBackgroundColor='var(--dcm-color-surface-hover, #F6F6F6)'
                      customSelectedColor='var(--dcm-checkout-text-color, #5D666D)'
                      width='100%'
                      hasError={!!Checkout.getError({ match: 'reservation' }).error}
                      tagPrefix='time-select'
                    />
                  </SelectContainer>
                )}
              </SelectionContainerV2>
            )}
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  ));
}

const SelectContainer = styled.div`
  display: flex;
  width: ${({ advancedDayOrderingEnabled }) => (advancedDayOrderingEnabled ? '168px' : '100%')};
  @media (max-width: 450px) {
    display: flex;
    width: 100%;
  }
`;

const SummaryText = styled.span`
  color: var(--dcm-checkout-text-color, #5d666d);
  font-size: 14px;
  padding-top: 2px;

  strong {
    font-weight: bold;
  }
`;

const OptionRow = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const OptionBold = styled.span`
  font-weight: bold;
`;

const OptionFee = styled.span`
  white-space: nowrap;
  color: var(--dcm-checkout-text-color, #5d666d);
`;

const CollapsedFee = styled.span`
  margin-left: auto;
  margin-right: 16px;
  white-space: nowrap;
  padding-top: 2px;
  font-size: 14px;
  color: var(--dcm-checkout-text-color, #5d666d);
`;

const InnerSummaryText = styled(SummaryText)`
  display: inline-block;
  margin-bottom: 20px;

  @media (max-width: 450px) {
    max-width: 80%;
  }
`;

const Bold = styled.strong`
  font-weight: bold;
`;

const SelectionContainerV2 = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: row;
  margin-bottom: 20px;
  @media (max-width: 450px) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    height: 100px;
  }
`;

const NoOptionsMessage = styled.div`
  color: #98a2a9;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
`;

const NoAvailableDatesMessage = styled.div`
  color: #98a2a9;
  font-size: 13px;
  line-height: 20px;
  width: 100%;
  padding: 8px 0;
`;

export default forwardRef(ScheduledOrders);
